import { IAnalyticsLayout } from "@formant/core/model/IAnalyticsLayout";
import { IDashboardLayout } from "@formant/core/model/IDashboardLayout";
import { IObserveLayout } from "@formant/core/model/IObserveLayout";
import { ITeleopLayout } from "@formant/core/model/ITeleopLayout";

import { getViewId } from "../utils/view";

export interface IModuleMemoryHook {
    saveState: (streamName: string, state: Record<string, any>) => void;
    rehydrate: (streamName: string) => Record<string, any> | null;
    saveLayout: (
        layout:
            | IObserveLayout
            | ITeleopLayout
            | IAnalyticsLayout
            | IDashboardLayout,
    ) => void;
    getLayout: () => IObserveLayout | ITeleopLayout | IAnalyticsLayout | null;
    clearLayout: (id: string) => void;
    destroy: () => void;
}

const PREFIX = "view_layout_v2";

// delete all keys with prefix
for (const key in localStorage) {
    if (key.startsWith(PREFIX)) {
        localStorage.removeItem(key);
    }
}

const useModuleMemory = (): IModuleMemoryHook => {
    // Function to save state to localStorage
    const saveState = (
        streamName: string,
        state: Record<string, any>,
    ): void => {
        const viewId = getViewId();
        try {
            const serializedState = JSON.stringify({
                streamName,
                viewId,
                state,
            });
            localStorage.setItem(`${streamName}_${viewId}`, serializedState);
        } catch (error) {
            window.console.error("Error saving state to localStorage:", error);
        }
    };

    // Function to retrieve state from localStorage
    const rehydrate = (streamName: string): Record<string, any> | null => {
        const viewId = getViewId();
        try {
            const serializedState = localStorage.getItem(
                `${streamName}_${viewId}`,
            );
            if (serializedState) {
                const { state } = JSON.parse(serializedState);
                return state;
            }
            return null;
        } catch (error) {
            window.console.error(
                "Error retrieving state from localStorage:",
                error,
            );
            return null;
        }
    };

    // Function to save a view's layout
    const saveLayout = (
        layout:
            | IObserveLayout
            | ITeleopLayout
            | IAnalyticsLayout
            | IDashboardLayout,
    ): void => {
        const viewId = getViewId();
        try {
            const serializedLayout = JSON.stringify(layout);
            localStorage.setItem(`${PREFIX}_${viewId}`, serializedLayout);
        } catch (error) {
            window.console.error("Error saving layout to localStorage:", error);
        }
    };

    // Function to get a view's layout
    const getLayout = ():
        | IObserveLayout
        | ITeleopLayout
        | IAnalyticsLayout
        | null => {
        try {
            const viewId = getViewId();
            const serializedLayout = localStorage.getItem(
                `${PREFIX}_${viewId}`,
            );
            if (serializedLayout) {
                const layout = JSON.parse(serializedLayout);
                return layout as
                    | IObserveLayout
                    | ITeleopLayout
                    | IAnalyticsLayout;
            }
            return null;
        } catch (error) {
            window.console.error(
                "Error retrieving layout from localStorage:",
                error,
            );
            return null;
        }
    };

    const clearLayout = (id: string): void => {
        localStorage.removeItem(`${PREFIX}_${id}`);
    };

    // Cleanup function to clear localStorage when unmounting
    const destroy = (): void => {
        const viewId = getViewId();
        localStorage.removeItem(`${PREFIX}_${viewId}`);
    };

    return {
        saveState,
        rehydrate,
        getLayout,
        saveLayout,
        clearLayout,
        destroy,
    };
};

export default useModuleMemory;
